import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { OperacionesClienteFinal, TokenCliente } from '../api/ops/clienteFinal';
import type { CatalogoPublico, HuecoCliente, MiCita, PresupuestoCliente, ResultadoHuecosCliente, ServicioPublico } from '../api/ops/citasCliente';
import { claveIdempotencia } from '../api/comun';
import { ErrorApi } from '../api/problema';
import { Chip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { dinero } from '../logica/dinero';
import { navegador } from '../logica/navegador';
import { CLAVE_CITA_PEDIDA, conDesde, diaCorto, diaLargo, duracion, esColor, estiloDeMarca, faltanPorResponder, horaEn, hoyLocal, inicialDe, mayuscula, momentoEn, ordenadas, porCategoria, RUTA_MIS_CITAS, serviciosPedidos, temaDe, textoConfirmacion, textoSinHueco, type Respuesta, type Respuestas, type TemaMarca } from './citas';
import { ElegirHora, type OpcionProfesional } from './ElegirHora';
import { cierreDeEntrada, esBaja, leerEntrada, type Cierre } from './entradaCerrada';
import { Identificarse } from './Identificarse';
import { AvisoPc, Baldosa, BotonPc, LegalCaptcha, Pagina } from './Pagina';
import { textoDeError } from './logica';
import './citas.css';

/**
 * Pedir cita (9.9, G647, G672): la página del cliente que abren la web del negocio (13.7, `/pedir-cita/{slug}`) y su widget
 * (13.8, `/pedir-cita?clave=pk_…&marco=widget&tema=&acento=`), con `?servicio=` y `?profesional=` (opciones
 * `service_id` y `professional_id` de `citas.pedir_cita`, G574). Con la marca del negocio (G618): en la web, el color de la
 * marca y tema claro (G614); en el widget, su tema y su acento, sin cabecera. Nunca el negro ni la ola de Reservaria.
 *
 * Cuatro pasos: servicios con sus preguntas · con quién y cuándo · tus datos (2.9, sin él si ya hay token) · revisar y
 * pedir (`bookAppointment` con captcha e `Idempotency-Key`; `409` con alternativas). Con señal, la página de pago
 * (`payment_action.url`, 30 minutos, G39) y la vuelta con `?vuelta=1`, que vuelve a leer la cita; sin señal, «Ya tienes tu
 * cita». Dinero, huecos y plazos los da la API. Con la entrada pública cerrada, los cuatro estados de 8.11 y 10.13
 * (`entradaCerrada.ts`, G983): pausado, temporada con «vuelve a abrir el…», retirada y baja (`410`, sin marca ni nombre).
 */
export type OrigenPedirCita = { tipo: 'web'; slug: string } | { tipo: 'widget'; clave: string };

interface Props {
    ops: OperacionesClienteFinal;
    origen: OrigenPedirCita;
    busqueda: URLSearchParams;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    /** Hay token de cliente vigente en la zona. */
    identificado: boolean;
    captcha: (accion: string) => Promise<string | null>;
    onToken: (t: TokenCliente) => void;
    /** «No soy yo»: revoca el token y lo olvida. */
    onSalir: () => void;
    /** Guardar la cita pedida mientras se paga (sessionStorage; las pruebas dan uno en memoria). */
    almacen?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
    ahora?: () => number;
}

interface Negocio {
    id: string;
    nombre: string;
    clave: string;
    privacidad: string;
    zona: string;
    widget: boolean;
    estilo: CSSProperties;
    oscuro: boolean;
    servicio: string | null;
    profesional: string | null;
}

type Carga =
    | { tipo: 'cargando' }
    | { tipo: 'error' }
    | { tipo: 'no-disponible'; nombre: string; estilo?: CSSProperties; widget: boolean }
    // G983: la entrada pública cerrada (pausado, temporada o retirada) y la baja (`410`), como 8.11 y 10.13.
    | { tipo: 'cerrada'; nombre: string; estilo: CSSProperties; cierre: Cierre }
    | { tipo: 'baja' }
    | { tipo: 'ok'; negocio: Negocio; catalogo: CatalogoPublico };
type Paso = 'servicios' | 'hora' | 'datos' | 'confirmar' | 'hecho' | 'vuelta';
type Resultado = { tipo: 'hecha'; cita: MiCita } | { tipo: 'procesando'; cita: MiCita } | { tipo: 'liberada' } | { tipo: 'rechazado' };
interface Pedida {
    perfil: string;
    cita: string;
    pago: string | null;
}

/** La tarjeta de la página cerrada (G983), la misma de la reserva pública (8.11). */
function TarjetaCerrada({ icono, chip, titulo, texto, onMisCitas }: { icono: NombreIcono; chip: string; titulo: string; texto: string; onMisCitas: () => void }) {
    return (
        <section className="rv-pc-card">
            <div className="rv-pc-baldosa-fila"><Baldosa icono={icono} fondo="var(--rv-paper)" color="var(--rv-ink)" /><Chip estado="neutral">{chip}</Chip></div>
            <div className="rv-pc-card__cab"><h1>{titulo}</h1><span>{texto}</span></div>
            <BotonPc texto="Ver mis citas" icono="calendar-check" onClick={onMisCitas} />
        </section>
    );
}

const COLOR_POR_DEFECTO = '#1A1A1A';

function prefiereOscuro(): boolean {
    try {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch {
        return false;
    }
}

function guardado(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> {
    try {
        return window.sessionStorage;
    } catch {
        const m = new Map<string, string>();
        return { getItem: (k) => m.get(k) ?? null, setItem: (k, v) => { m.set(k, v); }, removeItem: (k) => { m.delete(k); } };
    }
}

/** La dirección a la que vuelve el pago: esta misma página con `?vuelta=1` (solo https, como la firma, G475). */
function vueltaDe(href: string): string | null {
    try {
        const u = new URL(href);
        if (u.protocol !== 'https:') return null;
        u.searchParams.set('vuelta', '1');
        u.hash = '';
        return u.toString();
    } catch {
        return null;
    }
}

export function PedirCita({ ops, origen, busqueda, navegar, identificado, captcha, onToken, onSalir, almacen: dado, ahora = Date.now }: Props) {
    const almacen = useMemo(() => dado ?? guardado(), [dado]);
    const [carga, setCarga] = useState<Carga>({ tipo: 'cargando' });
    const [intento, setIntento] = useState(0);
    const [paso, setPaso] = useState<Paso>(() => (busqueda.get('vuelta') === '1' && almacen.getItem(CLAVE_CITA_PEDIDA) !== null ? 'vuelta' : 'servicios'));
    const [elegidos, setElegidos] = useState<string[]>([]);
    const [respuestas, setRespuestas] = useState<Respuestas>({});
    const [faltan, setFaltan] = useState<string[]>([]);
    const [pista, setPista] = useState('');
    const [presupuestoLeido, setPresupuesto] = useState<{ clave: string; p: PresupuestoCliente | null } | null>(null);
    const [profesional, setProfesional] = useState<string | null>(null);
    const [dia, setDia] = useState(() => hoyLocal(new Date(ahora())));
    const [huecosLeidos, setHuecos] = useState<{ clave: string; r: ResultadoHuecosCliente | null } | null>(null);
    const [buscar, setBuscar] = useState(0);
    const [hueco, setHueco] = useState<HuecoCliente | null>(null);
    const [perfil, setPerfil] = useState<string | null>(null);
    const [quien, setQuien] = useState<{ nombre: string; contacto: string } | null>(null);
    const [finalLeido, setFinal] = useState<{ clave: string; p: PresupuestoCliente | null } | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [captchaMal, setCaptchaMal] = useState(false);
    const [ocupada, setOcupada] = useState<HuecoCliente[] | null>(null);
    const [errorPedir, setErrorPedir] = useState<string | null>(null);
    const [resultado, setResultado] = useState<Resultado | null>(null);
    const [comprobando, setComprobando] = useState(false);
    const idem = useRef<string>(claveIdempotencia());

    // Negocio, marca y catálogo.
    useEffect(() => {
        let vivo = true;
        const leer = async (): Promise<Carga> => {
            if (origen.tipo === 'web') {
                // G885: la entrada pública del negocio, aunque no tenga la web publicada.
                const entrada = await leerEntrada(() => ops.getPublicEntry(origen.slug));
                const color = entrada.brand?.primary_color ?? null;
                const estilo = estiloDeMarca(esColor(color) ? color : COLOR_POR_DEFECTO, false);
                const cierre = cierreDeEntrada(entrada);
                if (cierre !== null) return { tipo: 'cerrada', nombre: entrada.business.name, estilo, cierre };
                const negocio: Negocio = {
                    id: entrada.business.business_id,
                    nombre: entrada.business.name,
                    clave: entrada.api_key,
                    privacidad: entrada.privacy_url,
                    zona: entrada.business.timezone,
                    widget: false,
                    estilo,
                    oscuro: false,
                    servicio: busqueda.get('servicio'),
                    profesional: busqueda.get('profesional'),
                };
                return conCatalogo(negocio);
            }
            const w = await ops.getPublicWidget(origen.clave);
            const acento = busqueda.get('acento');
            const color = esColor(acento) ? acento : esColor(w.accent_color) ? w.accent_color : COLOR_POR_DEFECTO;
            const tema: TemaMarca = temaDe(busqueda.get('tema')) ?? w.theme ?? 'light';
            const oscuro = tema === 'dark' || (tema === 'auto' && prefiereOscuro());
            const estilo = estiloDeMarca(color, oscuro);
            const nombre = w.business_name ?? '';
            if (w.status !== 'active') return { tipo: 'no-disponible', nombre, estilo, widget: true };
            const opciones = (w.options ?? {}) as Record<string, unknown>;
            const texto = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
            return conCatalogo({
                id: w.business_id,
                nombre,
                clave: origen.clave,
                // G869: «Privacidad de [negocio]» en el pie dentro del widget.
                privacidad: w.privacy_url ?? '',
                zona: '',
                widget: true,
                estilo,
                oscuro,
                servicio: busqueda.get('servicio') ?? texto(opciones.service_id),
                profesional: busqueda.get('profesional') ?? texto(opciones.professional_id),
            });
        };
        const conCatalogo = async (negocio: Negocio): Promise<Carga> => {
            try {
                const catalogo = await ops.getAppointmentCatalog(negocio.id, negocio.clave);
                if (catalogo.services.length === 0) return { tipo: 'no-disponible', nombre: negocio.nombre, estilo: negocio.estilo, widget: negocio.widget };
                return { tipo: 'ok', negocio: { ...negocio, zona: negocio.zona === '' ? catalogo.timezone : negocio.zona }, catalogo };
            } catch (e) {
                if (e instanceof ErrorApi && (e.estado === 404 || e.estado === 403)) return { tipo: 'no-disponible', nombre: negocio.nombre, estilo: negocio.estilo, widget: negocio.widget };
                throw e;
            }
        };
        leer()
            .then((c) => {
                if (!vivo) return;
                setCarga(c);
                if (c.tipo === 'ok') {
                    const s = c.catalogo.services.find((x) => x.id === c.negocio.servicio);
                    if (s !== undefined) setElegidos((ya) => (ya.length === 0 ? [s.id] : ya));
                    if (c.catalogo.customer_can_choose_professional && c.catalogo.professionals.some((p) => p.id === c.negocio.profesional)) setProfesional(c.negocio.profesional);
                }
            })
            .catch((e: unknown) => {
                if (!vivo) return;
                setCarga(esBaja(e) ? { tipo: 'baja' } : e instanceof ErrorApi && (e.estado === 404 || e.estado === 410) ? { tipo: 'no-disponible', nombre: '', widget: origen.tipo === 'widget' } : { tipo: 'error' });
            });
        return () => {
            vivo = false;
        };
        // La consulta solo cuenta al abrir.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, origen.tipo, origen.tipo === 'web' ? origen.slug : origen.clave, intento]);

    const ok = carga.tipo === 'ok' ? carga : null;
    const catalogo = ok?.catalogo ?? null;
    const negocio = ok?.negocio ?? null;
    const pedidos = useMemo(() => (catalogo === null ? [] : serviciosPedidos(elegidos, catalogo.services, respuestas)), [catalogo, elegidos, respuestas]);
    const completos = catalogo !== null && elegidos.length > 0 && faltanPorResponder(elegidos, catalogo.services, respuestas).length === 0;

    const clavePedidos = JSON.stringify(pedidos);
    const presupuesto = completos && presupuestoLeido?.clave === clavePedidos ? presupuestoLeido.p : null;

    // Duración, precio y señal con las respuestas (quoteAppointment), al cambiar lo elegido.
    useEffect(() => {
        if (negocio === null || !completos) return;
        let vivo = true;
        const t = setTimeout(() => {
            ops.citasDelNegocio(negocio.clave).quoteAppointment(negocio.id, { services: pedidos })
                .then((p) => { if (vivo) setPresupuesto({ clave: clavePedidos, p }); })
                .catch(() => { if (vivo) setPresupuesto({ clave: clavePedidos, p: null }); });
        }, 250);
        return () => {
            vivo = false;
            clearTimeout(t);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, clavePedidos, completos]);

    // Huecos del día (searchAppointmentSlots) en el paso 2.
    const claveHuecos = `${clavePedidos}|${profesional ?? ''}|${dia}|${String(buscar)}`;
    const huecos = huecosLeidos?.clave === claveHuecos ? huecosLeidos.r : null;
    const errorHuecos = huecosLeidos?.clave === claveHuecos && huecosLeidos.r === null;
    useEffect(() => {
        if (negocio === null || paso !== 'hora' || pedidos.length === 0) return;
        let vivo = true;
        ops.citasDelNegocio(negocio.clave).searchAppointmentSlots(negocio.id, { services: pedidos, professional_id: profesional, mode: 'list', from: dia, to: dia })
            .then((r) => { if (vivo) setHuecos({ clave: claveHuecos, r }); })
            .catch(() => { if (vivo) setHuecos({ clave: claveHuecos, r: null }); });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, paso, claveHuecos]);

    // Revisar: el presupuesto con la hora, para saber hasta cuándo se cancela gratis.
    const claveFinal = hueco === null ? '' : `${clavePedidos}|${hueco.starts_at}|${hueco.professional_id ?? ''}`;
    const final = finalLeido?.clave === claveFinal ? finalLeido.p : null;
    useEffect(() => {
        if (negocio === null || paso !== 'confirmar' || hueco === null) return;
        let vivo = true;
        ops.citasDelNegocio(negocio.clave).quoteAppointment(negocio.id, { services: pedidos, professional_id: profesional ?? hueco.professional_id ?? null, starts_at: hueco.starts_at })
            .then((p) => { if (vivo) setFinal({ clave: claveFinal, p }); })
            .catch(() => { if (vivo) setFinal({ clave: claveFinal, p: null }); });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, paso, hueco]);

    // Quién es (para «Tus datos») cuando ya había token.
    useEffect(() => {
        if (!identificado || quien !== null) return;
        let vivo = true;
        ops.getMyCustomerAccount()
            .then(({ datos }) => { if (vivo) setQuien({ nombre: datos.display_name, contacto: datos.phone ?? datos.email ?? '' }); })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, identificado, quien]);

    // La vuelta del pago: se vuelve a leer la cita.
    const consultar = (): void => {
        const texto = almacen.getItem(CLAVE_CITA_PEDIDA);
        const p = texto === null ? null : (JSON.parse(texto) as Pedida);
        if (p === null) return;
        ops.getMyAppointment(p.perfil, p.cita)
            .then((c) => {
                setResultado(
                    c.status === 'pending_payment' ? { tipo: 'procesando', cita: c } : c.status === 'expired' ? { tipo: 'liberada' } : c.status === 'cancelled' ? { tipo: 'rechazado' } : { tipo: 'hecha', cita: c },
                );
                if (c.status !== 'pending_payment') almacen.removeItem(CLAVE_CITA_PEDIDA);
            })
            .catch(() => { setResultado({ tipo: 'rechazado' }); })
            .finally(() => { setComprobando(false); });
    };
    const leerPedida = (): void => {
        setComprobando(true);
        consultar();
    };
    useEffect(() => {
        if (paso === 'vuelta' && resultado === null && identificado) consultar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paso, identificado]);

    if (carga.tipo === 'baja') {
        // G932: con `410` no llega la marca: colores de Reservaria y sin el nombre del negocio (D-052).
        return (
            <Pagina cabecera={{ tipo: 'negocio', nombre: 'Pedir cita' }} pie={{ tipo: 'reservaria' }} marca={estiloDeMarca(COLOR_POR_DEFECTO, false)} sinCabecera centrada>
                <TarjetaCerrada icono="unlink" chip="No disponible" titulo="Esta dirección ya no da citas" texto="Este negocio ya no da citas por internet con esta dirección. Si tienes una cita, la encuentras en «Mis citas»." onMisCitas={() => { navegar(RUTA_MIS_CITAS); }} />
            </Pagina>
        );
    }
    if (carga.tipo === 'cerrada') {
        const { cierre, nombre } = carga;
        const misCitas = () => { navegar(RUTA_MIS_CITAS); };
        const verla = 'Si ya tienes una cita, puedes verla o cancelarla.';
        return (
            <Pagina cabecera={{ tipo: 'negocio', nombre, linea: 'Pedir cita' }} pie={{ tipo: 'reservaria' }} marca={carga.estilo} centrada>
                {cierre.motivo === 'temporada' ? (
                    <TarjetaCerrada icono="calendar-x" chip="Cerrado por temporada" titulo="Cerrado por temporada" texto={cierre.vuelve === null ? verla : `${nombre} vuelve a abrir el ${diaLargo(cierre.vuelve)}. Desde entonces podrás pedir cita aquí. ${verla}`} onMisCitas={misCitas} />
                ) : cierre.motivo === 'retirada' ? (
                    <TarjetaCerrada icono="calendar-x" chip="Sin citas online" titulo="Esta página no está disponible" texto={`Ahora no se puede pedir cita con ${nombre} por internet. Ponte en contacto con ellos para pedirla. ${verla}`} onMisCitas={misCitas} />
                ) : (
                    <TarjetaCerrada icono="calendar-x" chip="Sin citas online" titulo="Ahora no se puede pedir cita online" texto={`${nombre} no está dando citas por internet en este momento. Ponte en contacto con ellos para pedirla. ${verla}`} onMisCitas={misCitas} />
                )}
            </Pagina>
        );
    }
    if (carga.tipo === 'cargando' || carga.tipo === 'error' || carga.tipo === 'no-disponible') {
        const marca = carga.tipo === 'no-disponible' && carga.estilo !== undefined ? carga.estilo : estiloDeMarca(COLOR_POR_DEFECTO, false);
        const nombre = carga.tipo === 'no-disponible' ? carga.nombre : '';
        return (
            <Pagina cabecera={{ tipo: 'negocio', nombre: nombre === '' ? 'Pedir cita' : nombre, linea: 'Pedir cita' }} pie={{ tipo: 'reservaria' }} marca={marca} sinCabecera={origen.tipo === 'widget' || nombre === ''} centrada={carga.tipo !== 'cargando'}>
                {carga.tipo === 'cargando' ? (
                    <section className="rv-pc-card" aria-busy="true" aria-label="Cargando">
                        <div className="rv-pc-esqueleto" style={{ height: 18, width: '46%' }} />
                        <div className="rv-pc-esqueleto" style={{ height: 48 }} />
                        <div className="rv-pc-esqueleto" style={{ height: 48 }} />
                    </section>
                ) : carga.tipo === 'error' ? (
                    <section className="rv-pc-card">
                        <Baldosa icono="triangle-alert" fondo="var(--rv-red-soft)" color="var(--rv-red)" />
                        <div className="rv-pc-card__cab"><h1>No hemos podido cargar los servicios</h1><span>Puede ser la conexión. No se ha perdido nada. Prueba de nuevo.</span></div>
                        <BotonPc texto="Reintentar" icono="refresh-cw" onClick={() => { setCarga({ tipo: 'cargando' }); setIntento((i) => i + 1); }} />
                    </section>
                ) : (
                    <section className="rv-pc-card">
                        <div className="rv-pc-baldosa-fila"><Baldosa icono="store" fondo="var(--rv-paper)" color="var(--rv-ink)" /><Chip estado="neutral">No disponible</Chip></div>
                        <div className="rv-pc-card__cab"><h1>Ahora no se puede pedir cita aquí</h1><span>{`${nombre === '' ? 'Este negocio' : nombre} no tiene activas las citas por internet. Llámales o pásate por allí.`}</span></div>
                    </section>
                )}
            </Pagina>
        );
    }

    const { negocio: n, catalogo: cat } = carga;
    const sabiaQuien = identificado;
    const pasos: Paso[] = sabiaQuien ? ['servicios', 'hora', 'confirmar'] : ['servicios', 'hora', 'datos', 'confirmar'];
    const ceja = (p: Paso) => {
        const i = Math.max(0, pasos.indexOf(p));
        return (
            <span className="rv-ct-ceja">
                <span>{`Paso ${String(i + 1)} de ${String(pasos.length)}`}</span>
                <span className="rv-ct-barra" aria-hidden="true"><span style={{ width: `${(((i + 1) / pasos.length) * 100).toFixed(0)}%` }} /></span>
            </span>
        );
    };
    const servicio = (id: string): ServicioPublico | undefined => cat.services.find((s) => s.id === id);
    const nombres = elegidos.map((id) => servicio(id)?.name ?? '').join(' + ');
    const minutos = presupuesto?.total_duration_minutes ?? elegidos.reduce((t, id) => t + (servicio(id)?.base_duration_minutes ?? 0), 0);
    const elegibles: OpcionProfesional[] = (presupuesto?.eligible_professionals ?? cat.professionals.filter((p) => elegidos.every((id) => (p.service_ids ?? servicio(id)?.professional_ids ?? []).includes(id))))
        .map((p) => ({ id: p.id, nombre: p.display_name }));
    const opcionesPro: OpcionProfesional[] = cat.customer_can_choose_professional ? [{ id: null, nombre: 'Cualquiera' }, ...elegibles] : [];
    const nombrePro = (id: string | null | undefined): string => elegibles.find((p) => p.id === id)?.nombre ?? cat.professionals.find((p) => p.id === id)?.display_name ?? '';
    const proDelHueco = hueco === null ? '' : profesional !== null ? nombrePro(profesional) : (hueco.professional_display_name ?? nombrePro(hueco.professional_id));

    const alternar = (id: string) => {
        const esta = elegidos.includes(id);
        if (!esta && elegidos.length >= 10) {
            setPista('Como mucho 10 servicios en una cita.');
            return;
        }
        setElegidos(esta ? elegidos.filter((x) => x !== id) : [...elegidos, id]);
        setPista('');
        setHueco(null);
    };
    const responder = (pregunta: string, r: Respuesta) => {
        setRespuestas({ ...respuestas, [pregunta]: r });
        setFaltan(faltan.filter((q) => q !== pregunta));
        setPista('');
        setHueco(null);
    };
    const aLaHora = () => {
        if (elegidos.length === 0) {
            setPista('Elige al menos un servicio.');
            return;
        }
        const f = faltanPorResponder(elegidos, cat.services, respuestas);
        if (f.length > 0) {
            setFaltan(f);
            setPista('Falta responder una pregunta.');
            return;
        }
        if (profesional !== null && !elegibles.some((p) => p.id === profesional) && presupuesto !== null) setProfesional(null);
        setPaso('hora');
    };
    const identificarAqui = () => {
        setOcupado(true);
        setErrorPedir(null);
        ops.identifyCustomerInBusiness(n.id, n.clave, { source: n.widget ? 'widget' : 'web' })
            .then((r) => {
                setPerfil(r.customer_id);
                setPaso('confirmar');
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 401) {
                    onSalir();
                    setPaso('datos');
                } else {
                    setErrorPedir(textoDeError(e));
                }
            })
            .finally(() => { setOcupado(false); });
    };
    const seguirDeHora = () => {
        if (hueco === null) return;
        if (identificado && perfil !== null) setPaso('confirmar');
        else if (identificado) identificarAqui();
        else setPaso('datos');
    };
    const elegirHueco = (h: HuecoCliente) => {
        if (h.local_date !== dia) setDia(h.local_date);
        setHueco(h);
        setOcupada(null);
        idem.current = claveIdempotencia();
    };
    const noSoyYo = () => {
        setPerfil(null);
        setQuien(null);
        onSalir();
        setPaso('datos');
    };
    const pedir = async () => {
        if (hueco === null || perfil === null || ocupado) return;
        setOcupado(true);
        setCaptchaMal(false);
        setOcupada(null);
        setErrorPedir(null);
        try {
            const vuelta = vueltaDe(window.location.href);
            const cita = await ops.bookAppointment(
                n.id,
                n.clave,
                {
                    local_date: hueco.local_date,
                    local_time: hueco.local_time,
                    services: pedidos,
                    professional_id: profesional,
                    customer_id: perfil,
                    locale: 'es-ES',
                    ...(vuelta === null ? {} : { return_url: vuelta }),
                },
                await captcha('pedir_cita'),
                idem.current,
            );
            if (cita.payment_action != null) {
                almacen.setItem(CLAVE_CITA_PEDIDA, JSON.stringify({ perfil, cita: cita.id, pago: cita.payment_action.url } satisfies Pedida));
                navegador.ir(cita.payment_action.url);
                return;
            }
            setResultado({ tipo: 'hecha', cita });
            setPaso('hecho');
        } catch (e) {
            if (e instanceof ErrorApi && e.codigo === 'captcha_required') {
                setCaptchaMal(true);
            } else if (e instanceof ErrorApi && (e.codigo === 'capacity_unavailable' || e.codigo === 'hold_expired')) {
                const alt = (e.problema as { alternatives?: HuecoCliente[] }).alternatives ?? [];
                setOcupada(alt.slice(0, 3));
                idem.current = claveIdempotencia();
            } else if (e instanceof ErrorApi && e.estado === 401) {
                noSoyYo();
            } else {
                setErrorPedir(textoDeError(e));
            }
        } finally {
            setOcupado(false);
        }
    };

    const volver = paso === 'hora' ? 'servicios' : paso === 'datos' ? 'hora' : paso === 'confirmar' ? (sabiaQuien ? 'hora' : 'datos') : null;
    const resumen = (paso === 'hora' || paso === 'datos') && (
        <section className="rv-pc-card rv-ct-resumen" aria-label="Tu cita">
            <div className="rv-ct-resumen__cab">
                <span className="rv-pc-ceja">Tu cita</span>
                <button type="button" className="rv-pc-l" onClick={() => { setPaso(paso === 'datos' ? 'hora' : 'servicios'); }}>
                    <Icono nombre="pencil" />
                    <span>Cambiar</span>
                </button>
            </div>
            <strong>{nombres}</strong>
            {paso === 'datos' && hueco !== null && (
                <span className="rv-ct-resumen__linea"><Icono nombre="calendar" /><span>{`${mayuscula(diaLargo(hueco.local_date))}, ${hueco.local_time} · con ${proDelHueco}`}</span></span>
            )}
            <span className="rv-ct-resumen__linea"><Icono nombre="clock" /><span>{`${duracion(minutos)}${presupuesto === null ? '' : ` · ${dinero(presupuesto.total_price)}`}`}</span></span>
        </section>
    );

    const pagina = (contenido: ReactNode, centrada = false) => (
        <Pagina
            cabecera={{ tipo: 'negocio', nombre: n.nombre, linea: 'Pedir cita' }}
            pie={n.widget && n.privacidad !== '' ? { tipo: 'negocio', nombre: n.nombre, privacidad: n.privacidad } : { tipo: 'reservaria' }}
            marca={n.estilo}
            sinCabecera={n.widget}
            centrada={centrada}
            volver={volver === null ? undefined : { texto: 'Volver', accion: () => { setOcupada(null); setCaptchaMal(false); setPaso(volver); } }}
        >
            {contenido}
        </Pagina>
    );

    // ---- Resultado: hecha, vuelta del pago ----
    if (paso === 'hecho' || paso === 'vuelta') {
        if (paso === 'vuelta' && !identificado) {
            return pagina(
                <section className="rv-pc-card">
                    <div className="rv-pc-card__cab"><h1>Tu sesión ha caducado</h1><span>Mira cómo ha quedado tu cita en «Mis citas».</span></div>
                    <BotonPc texto="Ver mis citas" icono="calendar" onClick={() => { navegar(RUTA_MIS_CITAS); }} />
                </section>,
                true,
            );
        }
        if (resultado === null) {
            return pagina(<section className="rv-pc-card" aria-busy="true" aria-label="Comprobando"><div className="rv-pc-esqueleto" style={{ height: 120 }} /></section>, true);
        }
        const deNuevo = () => {
            almacen.removeItem(CLAVE_CITA_PEDIDA);
            setResultado(null);
            setHueco(null);
            setPaso('servicios');
        };
        if (resultado.tipo === 'hecha') {
            const c = resultado.cita;
            const pro = c.services.find((s) => s.professional_display_name !== undefined)?.professional_display_name ?? proDelHueco;
            const firma = c.consent_status === 'requested' || c.consent_status === 'pending';
            return pagina(
                <section className="rv-pc-card" aria-labelledby="rv-ct-hecha-t">
                    <div className="rv-pc-baldosa-fila"><Baldosa icono="check" fondo="var(--rv-red)" color="var(--pc-on, #fff)" /><Chip estado="confirmed">Confirmada</Chip></div>
                    <div className="rv-pc-card__cab"><h1 id="rv-ct-hecha-t">Ya tienes tu cita</h1><span>{`Te esperamos el ${diaLargo(c.local_date)} a las ${c.local_time}.`}</span></div>
                    <div className="rv-ct-filas">
                        <div><span>Cuándo</span><span>{`${mayuscula(diaCorto(c.local_date))}, ${c.local_time}`}</span></div>
                        {pro !== '' && <div><span>Con</span><span>{pro}</span></div>}
                        <div><span>Servicios</span><span>{c.services.map((s) => s.name).join(' y ')}</span></div>
                        {c.deposit !== undefined && c.deposit.amount > 0 ? (
                            <>
                                <div><span>Señal pagada</span><span>{dinero(c.deposit)}</span></div>
                                {/* G868: lo que queda en el centro lo calcula la API (`amount_due_at_venue`); el panel no resta. */}
                                {c.amount_due_at_venue != null && <div><span>{`En ${n.nombre}`}</span><span>{dinero(c.amount_due_at_venue)}</span></div>}
                            </>
                        ) : (
                            <div><span>{`Total, en ${n.nombre}`}</span><span>{dinero(c.total_price)}</span></div>
                        )}
                        <div><span>Código</span><span>{c.code}</span></div>
                    </div>
                    <div className="rv-pc-lineas">
                        <div className="rv-pc-linea"><Icono nombre="message-circle" /><span>{textoConfirmacion(c)}</span></div>
                        {firma && <div className="rv-pc-linea"><Icono nombre="signature" /><span>{`También te llega el consentimiento para firmar. Fírmalo antes del día de la cita: así no esperas en ${n.nombre}.`}</span></div>}
                    </div>
                    <div className="rv-pc-acciones">
                        {firma && <BotonPc texto="Firmar el consentimiento" icono="pen-line" onClick={() => { navegar(`/mis-documentos?negocio=${encodeURIComponent(n.id)}`); }} />}
                        <BotonPc tipo={firma ? 'sec' : 'pri'} texto="Ver mis citas" icono="calendar" onClick={() => { navegar(RUTA_MIS_CITAS); }} />
                    </div>
                </section>,
                true,
            );
        }
        if (resultado.tipo === 'procesando') {
            const pago = (() => {
                const t = almacen.getItem(CLAVE_CITA_PEDIDA);
                return t === null ? null : (JSON.parse(t) as Pedida).pago;
            })();
            return pagina(
                <section className="rv-pc-card" aria-labelledby="rv-ct-proc-t">
                    <div className="rv-pc-baldosa-fila"><Baldosa icono="loader-circle" fondo="var(--rv-paper)" color="var(--rv-ink)" /><Chip estado="pending">En proceso</Chip></div>
                    <div className="rv-pc-card__cab"><h1 id="rv-ct-proc-t">Esperando a tu banco</h1><span>Tu banco todavía no ha confirmado el pago. No vuelvas a pagar si ya lo has hecho. Tu hora sigue guardada.</span></div>
                    <div className="rv-pc-acciones">
                        <BotonPc tipo="sec" texto="Comprobar ahora" icono="refresh-cw" ocupado={comprobando} ocupadoTexto="Comprobando…" onClick={leerPedida} />
                        {pago !== null && <BotonPc texto="Volver a la página de pago" icono="lock" onClick={() => { navegador.ir(pago); }} />}
                    </div>
                </section>,
                true,
            );
        }
        return pagina(
            <section className="rv-pc-card">
                <div className="rv-pc-baldosa-fila">
                    <Baldosa icono={resultado.tipo === 'liberada' ? 'hourglass' : 'x'} fondo={resultado.tipo === 'liberada' ? 'var(--rv-paper)' : 'var(--rv-red-soft)'} color={resultado.tipo === 'liberada' ? 'var(--rv-ink)' : 'var(--rv-red)'} />
                    <Chip estado="problem">{resultado.tipo === 'liberada' ? 'Caducada' : 'Rechazado'}</Chip>
                </div>
                <div className="rv-pc-card__cab">
                    <h1>{resultado.tipo === 'liberada' ? 'Se ha liberado la hora' : 'No se ha podido cobrar'}</h1>
                    <span>{resultado.tipo === 'liberada' ? 'Han pasado los 30 minutos sin pagar la señal. No te hemos cobrado nada. Si sigue libre, puedes volver a pedirla.' : 'No te hemos cobrado nada y la hora ya no está guardada. Si todavía quieres, vuelve a pedirla.'}</span>
                </div>
                <BotonPc texto={resultado.tipo === 'liberada' ? 'Volver a pedir cita' : `Volver a ${n.nombre}`} icono="arrow-right" onClick={deNuevo} />
            </section>,
            true,
        );
    }

    // ---- Paso 1: servicios y preguntas ----
    if (paso === 'servicios') {
        const pago = presupuesto?.payment;
        return pagina(
            <>
                <section className="rv-pc-card" aria-labelledby="rv-ct-sv-t">
                    <div className="rv-pc-card__cab">
                        {ceja('servicios')}
                        <h1 id="rv-ct-sv-t">¿Qué te hacemos?</h1>
                        <span>Elige uno o varios servicios. Van seguidos, con la misma persona.</span>
                    </div>
                    {porCategoria(cat.services).map((g) => (
                        <div key={g.nombre} className="rv-ct-cat">
                            {g.nombre !== '' && <span>{g.nombre}</span>}
                            {g.servicios.map((s) => {
                                const on = elegidos.includes(s.id);
                                return (
                                    <div key={s.id} className="rv-ct-servicio">
                                        <button type="button" className="rv-ct-servicio__fila" aria-pressed={on} onClick={() => { alternar(s.id); }}>
                                            <span>
                                                <strong>{s.name}</strong>
                                                <small>{`${duracion(s.base_duration_minutes)} · ${conDesde(s) ? 'desde ' : ''}${dinero(s.price_from)}`}</small>
                                                {s.requires_consent === true && <span className="rv-ct-firma"><Icono nombre="signature" /><span>Pide firmar un consentimiento</span></span>}
                                            </span>
                                            <span aria-hidden="true" className="rv-ct-marca"><Icono nombre={on ? 'check' : 'plus'} /></span>
                                        </button>
                                        {on && s.questions.length > 0 && (
                                            <div className="rv-ct-preguntas">
                                                {ordenadas(s.questions).map((q) => {
                                                    const r = respuestas[q.id] ?? {};
                                                    const multiple = q.kind === 'multiple_choice';
                                                    return (
                                                        <div key={q.id} className="rv-ct-pregunta" role="group" aria-label={q.label}>
                                                            <span>{q.label}{!q.required && <small> (opcional)</small>}</span>
                                                            {q.help_text !== undefined && q.help_text !== '' && <span className="rv-ct-pregunta__ayuda">{q.help_text}</span>}
                                                            {(q.kind === 'single_choice' || multiple) && (
                                                                <div role={multiple ? 'group' : 'radiogroup'} aria-label={q.label} className="rv-ct-pastillas">
                                                                    {(q.options ?? []).map((o) => {
                                                                        const sel = (r.opciones ?? []).includes(o.id);
                                                                        const extra = [o.minutes_delta > 0 ? `+${String(o.minutes_delta)} min` : '', o.price_delta.amount > 0 ? `+${dinero(o.price_delta)}` : ''].filter((x) => x !== '').join(', ');
                                                                        return (
                                                                            <button
                                                                                key={o.id}
                                                                                type="button"
                                                                                role={multiple ? 'checkbox' : 'radio'}
                                                                                aria-checked={sel}
                                                                                className="rv-ct-pastilla"
                                                                                onClick={() => { responder(q.id, { opciones: multiple ? (sel ? (r.opciones ?? []).filter((x) => x !== o.id) : [...(r.opciones ?? []), o.id]) : [o.id] }); }}
                                                                            >
                                                                                {multiple && <Icono nombre={sel ? 'check' : 'plus'} />}
                                                                                <span>{o.label}</span>
                                                                                {extra !== '' && <small>{extra}</small>}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                            {q.kind === 'number' && (
                                                                <label className="rv-ct-numero">
                                                                    <input
                                                                        className="rv-pc-select"
                                                                        type="number"
                                                                        inputMode="numeric"
                                                                        aria-label={q.label}
                                                                        min={q.number_min ?? 0}
                                                                        max={q.number_max ?? 1000}
                                                                        value={r.numero ?? 0}
                                                                        onChange={(e) => { responder(q.id, { numero: Math.max(q.number_min ?? 0, Math.min(q.number_max ?? 1000, Number(e.target.value) || 0)) }); }}
                                                                    />
                                                                    <small className="rv-ct-pregunta__ayuda">
                                                                        {[q.minutes_per_unit !== undefined ? `${String(q.minutes_per_unit)} min` : '', q.price_per_unit !== undefined ? dinero(q.price_per_unit) : ''].filter((x) => x !== '').join(' y ')}
                                                                        {' cada una'}
                                                                        {q.number_max !== undefined ? ` · máx. ${String(q.number_max)}` : ''}
                                                                    </small>
                                                                </label>
                                                            )}
                                                            {q.kind === 'text' && (
                                                                <textarea className="rv-ct-texto" aria-label="Tu respuesta" maxLength={q.text_max_length ?? 500} rows={2} value={r.texto ?? ''} onChange={(e) => { responder(q.id, { texto: e.target.value }); }} />
                                                            )}
                                                            {faltan.includes(q.id) && <span role="alert" className="rv-ct-error"><Icono nombre="circle-alert" /><span>Elige una opción para seguir.</span></span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </section>
                <section className="rv-pc-card" aria-live="polite">
                    <div className="rv-ct-suma">
                        <span>
                            <small>{elegidos.length === 0 ? 'Tu cita' : `${String(elegidos.length)} ${elegidos.length === 1 ? 'servicio' : 'servicios'} · ${duracion(minutos)}`}</small>
                            <strong>{elegidos.length === 0 ? 'Todavía no has elegido nada' : nombres}</strong>
                        </span>
                        <strong>{presupuesto === null ? '' : dinero(presupuesto.total_price)}</strong>
                    </div>
                    {elegidos.length > 0 && pago?.requires_payment === true && pago.deposit !== undefined && (
                        // G1024: con prepago completo (`amount_due_at_venue` nula, como en «Revisar», G989), sin «señal» ni «el resto».
                        <span className="rv-ct-aviso"><Icono nombre="credit-card" /><span>{pago.amount_due_at_venue == null ? `Al pedirla pagas ${dinero(pago.deposit)}.` : `Al pedirla pagas ${dinero(pago.deposit)} de señal; el resto, en ${n.nombre}.`}</span></span>
                    )}
                    <button type="button" className={`rv-pc-b rv-pc-b--pri${elegidos.length === 0 ? ' rv-ct-off' : ''}`} aria-disabled={elegidos.length === 0} onClick={aLaHora}>
                        <span>Elegir día y hora</span>
                        <Icono nombre="arrow-right" />
                    </button>
                    {pista !== '' && <span role="alert" className="rv-ct-error">{pista}</span>}
                </section>
            </>,
        );
    }

    // ---- Paso 2: con quién y cuándo ----
    if (paso === 'hora') {
        const sin = textoSinHueco(huecos?.unavailable_reason, dia, n.nombre, minutos, profesional === null ? null : nombrePro(profesional));
        return pagina(
            <>
                {resumen}
                <section className="rv-pc-card" aria-labelledby="rv-ct-hp-t">
                    <div className="rv-pc-card__cab">
                        {ceja('hora')}
                        <h1 id="rv-ct-hp-t">¿Con quién y cuándo?</h1>
                        <span>{`Tu cita dura ${duracion(minutos)}. Te enseñamos las horas libres.`}</span>
                    </div>
                    <ElegirHora
                        profesionales={opcionesPro}
                        profesional={profesional}
                        onProfesional={(p) => { setProfesional(p); setHueco(null); }}
                        dia={dia}
                        minDia={hoyLocal(new Date(ahora()))}
                        onDia={(d) => { setDia(d); setHueco(null); }}
                        resultado={huecos}
                        error={errorHuecos}
                        onReintentar={() => { setBuscar((b) => b + 1); }}
                        elegido={hueco?.starts_at ?? null}
                        onElegir={elegirHueco}
                        sinHueco={sin}
                        zona={n.zona}
                        tema={n.oscuro ? 'oscuro' : 'claro'}
                    />
                </section>
                {hueco !== null && (
                    <section className="rv-pc-card" aria-live="polite">
                        <span className="rv-ct-elegido">
                            <span aria-hidden="true"><Icono nombre="calendar-check" /></span>
                            <span>
                                <strong>{`${mayuscula(diaLargo(hueco.local_date))}, ${hueco.local_time}`}</strong>
                                <small>{`De ${hueco.local_time} a ${horaEn(hueco.ends_at, n.zona)} · ${profesional !== null ? `con ${proDelHueco}` : `te atiende ${proDelHueco} (si nadie coge antes esa hora)`}`}</small>
                            </span>
                        </span>
                        {errorPedir !== null && <AvisoPc texto={errorPedir} />}
                        <BotonPc texto="Seguir" icono="arrow-right" ocupado={ocupado} ocupadoTexto="Un momento…" onClick={seguirDeHora} />
                    </section>
                )}
            </>,
        );
    }

    // ---- Paso 3: tus datos (2.9) ----
    if (paso === 'datos') {
        return pagina(
            <>
                {resumen}
                <Identificarse
                    key="datos"
                    ops={ops}
                    contexto={{ tipo: 'negocio', negocio: { id: n.id, nombre: n.nombre, privacidad: n.privacidad, clavePublica: n.clave, origen: n.widget ? 'widget' : 'web', titulo: { titulo: 'Tus datos', sub: 'Te mandamos un código para comprobar que eres tú. Sin contraseñas.' } } }}
                    captcha={captcha}
                    onToken={(t) => {
                        setQuien({ nombre: t.account.display_name, contacto: t.account.phone ?? t.account.email ?? '' });
                        onToken(t);
                    }}
                    onIdentificadoEnNegocio={(r) => {
                        setPerfil(r.customer_id);
                        setPaso('confirmar');
                    }}
                    onNoSoyYo={noSoyYo}
                    ahora={ahora}
                    tema={n.oscuro ? 'oscuro' : 'claro'}
                />
            </>,
        );
    }

    // ---- Paso 4: revisar y pedir ----
    if (hueco === null) {
        return pagina(null);
    }
    const p = final ?? presupuesto;
    const pago = p?.payment;
    const senal = pago?.requires_payment === true && pago.deposit !== undefined && pago.deposit.amount > 0 ? pago.deposit : null;
    const conSenal = senal !== null;
    // G977: lo que queda por pagar en el centro lo da la cotización (`amount_due_at_venue`); el panel no resta.
    const resto = pago?.amount_due_at_venue ?? null;
    // G989: con prepago completo no queda nada en el centro (`amount_due_at_venue` nula): «Pagas ahora X €», sin «señal».
    const completo = conSenal && resto === null;
    const firmas = elegidos.map((id) => servicio(id)).filter((s): s is ServicioPublico => s?.requires_consent === true);
    const gratis = pago?.free_cancellation_until ?? null;
    return pagina(
        <>
            <section className="rv-pc-card" aria-labelledby="rv-ct-cf-t">
                <div className="rv-pc-card__cab">
                    {ceja('confirmar')}
                    <h1 id="rv-ct-cf-t">Revisa tu cita</h1>
                </div>
                <div className="rv-ct-filas">
                    <div><span>Cuándo</span><span>{mayuscula(diaLargo(hueco.local_date))}<small>{`De ${hueco.local_time} a ${horaEn(hueco.ends_at, n.zona)} (${duracion(p?.total_duration_minutes ?? minutos)})`}</small></span></div>
                    <div><span>Con</span><span>{proDelHueco}{profesional === null && <small>Elegiste «Cualquiera»: quien esté libre</small>}</span></div>
                    {(p?.segments ?? []).map((s) => (
                        <div key={s.seq}><span>{s.service_name ?? servicio(s.service_id)?.name ?? ''}</span><span>{dinero(s.price)}</span></div>
                    ))}
                    {p !== null && <div><span>Total</span><span>{dinero(p.total_price)}</span></div>}
                </div>
                {senal !== null && completo && (
                    <div className="rv-ct-senal">
                        <span>Pagas ahora</span>
                        <strong>{dinero(senal)}</strong>
                    </div>
                )}
                {senal !== null && !completo && (
                    <div className="rv-ct-senal">
                        <span>Hoy pagas la señal</span>
                        <strong>{dinero(senal)}</strong>
                        {resto !== null && <span>{`El resto, ${dinero(resto)}, en ${n.nombre}.`}</span>}
                    </div>
                )}
                {(gratis !== null || (pago?.conditions_text ?? '') !== '') && (
                    <div className="rv-ct-aviso">
                        <Icono nombre="scale" />
                        <span>
                            <b>Si cambias de idea</b>
                            {gratis !== null && <span>{`Puedes cambiarla o cancelarla gratis hasta el ${momentoEn(gratis, n.zona)}${conSenal ? (completo ? ' y te devolvemos lo que pagaste.' : ' y te devolvemos la señal.') : '.'}`}</span>}
                            {(pago?.conditions_text ?? '') !== '' && <span>{pago?.conditions_text}</span>}
                        </span>
                    </div>
                )}
                {firmas.length > 0 && (
                    <div className="rv-ct-aviso">
                        <Icono nombre="signature" />
                        <span>
                            <b>Hay que firmar un consentimiento</b>
                            <span>{`${firmas.map((s) => s.name).join(' y ')} pide tu firma. Te mandamos el enlace al confirmar; puedes firmarlo desde el móvil antes de la cita.`}</span>
                        </span>
                    </div>
                )}
            </section>
            <section className="rv-pc-card" aria-label="Tus datos">
                <span className="rv-pc-ceja">Tus datos</span>
                <div className="rv-ct-quien">
                    <span aria-hidden="true" className="rv-pc__avatar">{inicialDe(quien?.nombre ?? '')}</span>
                    <span><strong>{quien?.nombre ?? ''}</strong><small>{quien?.contacto ?? ''}</small></span>
                    <button type="button" className="rv-pc-b rv-pc-b--sec rv-pc-b--peq" onClick={noSoyYo}><Icono nombre="log-out" /><span>No soy yo</span></button>
                </div>
                <span className="rv-pc-nota">Te mandamos la confirmación con el enlace para cambiarla o cancelarla.</span>
            </section>
            {ocupada !== null && (
                <div role="alert" className="rv-ct-vacio">
                    <span><Icono nombre="circle-alert" /><span><span><b>Alguien acaba de coger esa hora.</b> No te hemos cobrado nada. Elige otra:</span></span></span>
                    <div className="rv-ct-alternativas">
                        {ocupada.map((a) => (
                            <button key={`${a.starts_at}-${a.professional_id ?? ''}`} type="button" className="rv-pc-b rv-pc-b--sec rv-pc-b--peq" onClick={() => { elegirHueco(a); }}>
                                <span>{`${mayuscula(diaCorto(a.local_date))} · ${a.local_time}${a.professional_display_name === undefined ? '' : ` · con ${a.professional_display_name}`}`}</span>
                                <Icono nombre="arrow-right" />
                            </button>
                        ))}
                        {ocupada.length === 0 && <button type="button" className="rv-pc-b rv-pc-b--sec rv-pc-b--peq" onClick={() => { setOcupada(null); setHueco(null); setPaso('hora'); }}><span>Elegir otra hora</span><Icono nombre="arrow-right" /></button>}
                    </div>
                </div>
            )}
            <div className="rv-pc-acciones">
                <BotonPc texto={senal !== null ? `Pedir la cita y pagar ${dinero(senal)}` : 'Pedir la cita'} icono={conSenal ? 'lock' : 'calendar-check'} ocupado={ocupado} ocupadoTexto="Pidiendo tu cita…" onClick={() => void pedir()} />
                {captchaMal && <AvisoPc texto="No hemos podido comprobar que no eres un robot. Vuelve a intentarlo: no hemos guardado ninguna hora." />}
                {errorPedir !== null && <AvisoPc texto={errorPedir} />}
                <LegalCaptcha />
            </div>
        </>,
    );
}
